import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { quoteFormSelector, experimentIdSelector, verticalLinkContentSelector, verticalCopyContentSelector } from 'ctm-selectors';
import { Text, Checkbox, withRules } from 'ctm-components';
import { useBrandContentFactory } from 'ctm-app/client/hooks/useBrandContentFactory';
import { pageContext, replaceBaseUrl } from 'ctm-util-functions';
import { connect, useSelector } from 'react-redux';
import { CallCentreHoursModal } from './call_centre_hours_dialog';
import { TrustedPartnersModal } from './trusted_partners_dialog';

/**
 * This is opt-in privacy component
 * @desc - a checkbox with opt-in text and opening hours modal button with opt-in text
 * @param {Object} props Component props
 * @return {Node} Slide Component
 */
const _OptinPrivacy = ({ dispatch, isToyPromoOn, isToyPromoOff, value }) => {
    const modalEventListeners = useRef([]);
    const companyName = useSelector((state) => verticalCopyContentSelector(state).get('application_details_opt_in_checkbox_brand_name'));
    const privacyPolicyUrl = useSelector((state) => verticalLinkContentSelector(state).get('application_details_opt_in_checkbox_privacy_policy_url'));
    const fsgUrl = useSelector((state) => verticalLinkContentSelector(state).get('application_details_opt_in_checkbox_financial_services_guide_url'));
    const termsOfUseUrl = useSelector((state) => verticalLinkContentSelector(state).get('application_details_opt_in_checkbox_terms_of_use_url'));
    const brandContentFactory = useBrandContentFactory();

    const handleOpeningHoursDialogClose = () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.callCentreHours',
            value: { open: false }
        });
    };

    const showOpeningHoursDialog = () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.callCentreHours',
            value: { open: true }
        });
    };

    const handleTrustedPartnersDialogClose = () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.trustedPartners',
            value: { open: false }
        });
    };

    const showTrustedPartnersDialog = () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.trustedPartners',
            value: { open: true }
        });
    };

    // Add callback to include event listeners for the modals off the elements found in the contactDetailOptInPrivacyMessage
    // currently only trustedPartnersOpen and callCentreHoursOpen is supported.
    const optInContentElementHandler = useCallback((node) => {
        const trustedPartnerHandler = (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            showTrustedPartnersDialog();
        };
        const callCenterHandler = (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            showOpeningHoursDialog();
        };

        // when component gets updated, it calls this ref function again.
        // node may become null on clean-up or certain situations.
        if (node !== null) {
            node.querySelector('[data-id=trustedPartnersOpen]').addEventListener('click', trustedPartnerHandler);
            node.querySelector('[data-id=callCentreHoursOpen]').addEventListener('click', callCenterHandler);
            modalEventListeners.current.push(() => { node.querySelector('[data-id=trustedPartnersOpen]').removeEventListener('click', trustedPartnerHandler); });
            modalEventListeners.current.push(() => { node.querySelector('[data-id=callCentreHoursOpen]').removeEventListener('click', callCenterHandler); });
        }

        // We are currently using React 18 which uses null to indicate the reference has been detached/unmounted
        // React 19 would move to a similar design as useEffect. Albeit React 18 introduced it as a canary feature but can't get it to work.
        // See https://18.react.dev/reference/react-dom/components/common#ref-callback-parameters
        // and https://react.dev/blog/2024/12/05/react-19#cleanup-functions-for-refs.
        if (node == null && modalEventListeners.current.length > 0) {
            modalEventListeners.current.forEach((unsubscribe) => unsubscribe());
            modalEventListeners.current = [];
        }
    },
    // Using companyName as that's the seem to trigger once all the brand switching has settled.
    // this ensures that the callback gets updated only after the new opt-in DOM element is rendered
    [companyName]);

    const renderPromoTAndCLink = () => {
        const baseAssetsPath = pageContext.getProps().models.pageSchemaModel.getBaseAssetsPath();
        const toyPromoTAndC = replaceBaseUrl('/evt/assets/pdf/meerkat-toys-promotion-terms-and-conditions.pdf', baseAssetsPath);
        if (isToyPromoOn && !isToyPromoOff) {
            // eslint-disable-next-line react/jsx-no-target-blank
            return `<a href="${toyPromoTAndC}" target="_blank" data-id="toyPromotionTAndCLink" rel="noreferrer">Promotion Terms & Conditions</a>`;
        }
        return null;
    };

    const renderLabel = () => {
        const toyPromo = renderPromoTAndCLink();
        const optInText = brandContentFactory.contactDetailOptInPrivacyMessage({ termsOfUseUrl, companyName, fsgUrl, privacyPolicyUrl, toyPromo });
        return (
            <Text
                ref={optInContentElementHandler}
                variant="underline_anchors"
                size="small"
                innerHTML={optInText} />
        );
    };
    const checkedActions = [
        {
            type: 'SET_FORM_VALUE',
            identifier: 'applicant.optInPrivacy',
            value: 'Y',
            stateKey: 'vertical.quote'
        }
    ];
    const uncheckedActions = [
        {
            type: 'SET_FORM_VALUE',
            identifier: 'applicant.optInPrivacy',
            value: 'N',
            stateKey: 'vertical.quote'
        }
    ];
    const schema = {
        stateKey: 'vertical.quote',
        field: {
            type: 'checkbox',
            UIComponent: 'checkbox_medium_rounded',
            labelEl: renderLabel(),
            variant: 'small_text',
            default: '',
            value: 'Y'
        },
        identifier: 'applicant.optInPrivacy',
        eventProps: {
            onChange: {
                checked: {
                    actions: checkedActions
                },
                unchecked: {
                    actions: uncheckedActions
                }
            }
        }
    };

    return (
        <>
            <TrustedPartnersModal closeModal={handleTrustedPartnersDialogClose} />
            <CallCentreHoursModal closeModal={handleOpeningHoursDialogClose} centres={['Car', 'Health']} />
            <Checkbox schema={schema} value={value} dispatch={dispatch} key={schema.identifier} />
        </>
    );
};

_OptinPrivacy.propTypes = {
    dispatch: PropTypes.func.isRequired,
    stateProps: PropTypes.shape({
        isOpen: PropTypes.bool
    }),
    value: PropTypes.string,
    isToyPromoOn: PropTypes.bool,
    isToyPromoOff: PropTypes.bool
};

_OptinPrivacy.defaultProps = {
    value: 'N'
};

/**
 * mapStateToProps - sets up the rules required by this component
 * @param {Object} state - Redux store state.
 * @return {Object} - The state attributes to add to props.
 */
const mapStateToProps = (state) => {
    const form = quoteFormSelector(state);
    const value = form.getIn(['applicant', 'optInPrivacy']);
    const experimentId = experimentIdSelector(state);

    // CAR_TOY_PROMO_YES and CAR_TOY_PROMO_NO are different A/B testing trigger values
    const isToyPromoOn = experimentId === 'CAR_TOY_PROMO_YES';
    const isToyPromoOff = experimentId === 'CAR_TOY_PROMO_NO';

    return {
        value,
        isToyPromoOn,
        isToyPromoOff
    };
};

const OptinPrivacy = withRules(connect(mapStateToProps)(_OptinPrivacy));

export { _OptinPrivacy, OptinPrivacy };
